/**
 * Zahlungen (Stitch: maestro_zahlungen): KPI-Reihe + Tabelle ueber alle Events.
 * Es gibt bewusst keinen globalen Payments-Endpunkt (tenant-scoped pro Event) —
 * daher werden die Zahlungen der Events parallel geladen und zusammengefuehrt.
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import type { Customer, Payment } from "../ui/types";
import { PAYMENT_STATUS, PAYMENT_TYPE_LABEL, type StatusMeta, formatDate, money } from "../ui/format";
import {
  EmptyState,
  ErrorNote,
  KpiCard,
  LoadingRows,
  PageHeader,
  StatusPill,
} from "../ui/components";

interface PaymentWithEvent extends Payment {
  eventLabel: string;
  eventNumber: string | null;
  customerId: string;
}

const STATUS_FILTERS: { key: "all" | "open" | "paid" | "failed" | "refunded"; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "open", label: "Offen" },
  { key: "paid", label: "Bezahlt" },
  { key: "failed", label: "Fehlgeschlagen" },
  { key: "refunded", label: "Erstattet" },
];

export default function PaymentsPage() {
  // H1 (Bughunt 2026-07-11): EIN aggregierter, tenant-scoped Endpunkt (Join events) statt des
  // fragilen N+1-Fan-outs (ein HTTP-Request je Event). Damit kann kein einzelnes Event mehr die
  // Gesamtliste entwerten — der irreführende "x von N konnten nicht geladen werden"-Banner entfällt.
  const paymentsQ = useApi<PaymentWithEvent[]>("/api/payments");
  const customersQ = useApi<Customer[]>("/api/customers");

  const customerName = useMemo(() => {
    const byCustomer = new Map((customersQ.data ?? []).map((c) => [c.id, c.name]));
    return (customerId: string) => byCustomer.get(customerId) || "–";
  }, [customersQ.data]);

  // CX-Audit 2026-07-17: Status-Filter aus der URL (?filter=open|paid|…) — Dashboard-KPIs
  // („Offene Beträge", „Umsatz Monat") verlinken gezielt hierher; Reload/Zurueck erhält den Filter.
  const [params, setParams] = useSearchParams();
  const urlFilter = params.get("filter");
  const statusFilter: (typeof STATUS_FILTERS)[number]["key"] = STATUS_FILTERS.some((f) => f.key === urlFilter)
    ? (urlFilter as (typeof STATUS_FILTERS)[number]["key"])
    : "all";
  const setStatusFilter = (key: (typeof STATUS_FILTERS)[number]["key"]) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === "all") next.delete("filter");
        else next.set("filter", key);
        return next;
      },
      { replace: true },
    );
  // L9 (Bughunt 2026-07-11): echte Tabellen-Textsuche (Kunde/Beleg). Das ⌘K-Kopffeld im AppShell
  // ist die globale Palette und filtert die Liste NICHT — hier eine eigene, lokale Suche.
  // CX-Audit 2026-07-17: ?q= als Startwert — die Kunden-KPI „Offene Posten" verlinkt mit
  // vorbefüllter Kundensuche hierher.
  const [search, setSearch] = useState(() => params.get("q") ?? "");

  const error = paymentsQ.error;
  const loading = paymentsQ.loading;
  const allRows = paymentsQ.data ?? [];
  const now = new Date();
  const overdueIds = new Set(
    allRows.filter((p) => ["pending", "processing"].includes(p.status) && p.dueDate && new Date(p.dueDate) < now).map((p) => p.id),
  );
  /** H11: "Erstattet" blau statt neutral, "Überfällig" (abgeleitet aus Faelligkeit) rot statt amber. */
  function paymentMeta(p: PaymentWithEvent): StatusMeta {
    if (overdueIds.has(p.id)) return { label: "Überfällig", tone: "danger" };
    if (p.status === "refunded") return { label: "Erstattet", tone: "info" };
    return PAYMENT_STATUS[p.status] ?? { label: p.status, tone: "neutral" };
  }
  const statusCounts = useMemo(() => {
    // N-5: Zähler „Offen" muss zur gefilterten Liste passen — die zeigt ALLE pending/processing
    // (inkl. überfällige), also hier nicht zusätzlich nach overdue filtern.
    const open = allRows.filter((p) => ["pending", "processing"].includes(p.status)).length;
    return {
      all: allRows.length,
      open,
      paid: allRows.filter((p) => p.status === "succeeded").length,
      failed: allRows.filter((p) => p.status === "failed").length,
      refunded: allRows.filter((p) => p.status === "refunded").length,
    };
  }, [allRows]);
  const rows = useMemo(() => {
    let list = allRows;
    if (statusFilter === "open") list = list.filter((p) => ["pending", "processing"].includes(p.status));
    else if (statusFilter === "paid") list = list.filter((p) => p.status === "succeeded");
    else if (statusFilter === "failed") list = list.filter((p) => p.status === "failed");
    else if (statusFilter === "refunded") list = list.filter((p) => p.status === "refunded");
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [customerName(p.customerId), p.eventNumber ?? "", p.eventLabel].some((s) => s.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [allRows, statusFilter, search, customerName]);
  const monthPaid = allRows
    .filter(
      (p) =>
        p.status === "succeeded" &&
        p.paidAt &&
        new Date(p.paidAt).getMonth() === now.getMonth() &&
        new Date(p.paidAt).getFullYear() === now.getFullYear(),
    )
    .reduce((s, p) => s + p.amountCents, 0);
  const open = allRows.filter((p) => ["pending", "processing"].includes(p.status));
  const openSum = open.reduce((s, p) => s + p.amountCents, 0);
  const overdue = open.filter((p) => overdueIds.has(p.id));
  const overdueSum = overdue.reduce((s, p) => s + p.amountCents, 0);
  const refunded = allRows
    .filter((p) => p.status === "refunded")
    .reduce((s, p) => s + p.amountCents, 0);
  const monthLabel = now.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <>
      <PageHeader
        title="Zahlungen"
        subtitle={`${monthLabel} · Übersicht aller Transaktionen über alle Veranstaltungen.`}
      />

      {/* KPI-Reihe (Stitch: Bezahlt Monat / Offen / Erstattet) — H9: Icon-Farben semantisch */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard label={`Bezahlt · ${monthLabel}`} value={money(monthPaid)} icon="payments" iconTone="success" />
        <KpiCard
          label={`Offen · ${open.length} Rechnung${open.length === 1 ? "" : "en"}`}
          value={money(openSum)}
          icon="account_balance_wallet"
          iconTone="warn"
          delta={overdue.length > 0 ? { text: `${overdue.length} überfällig`, tone: "down" } : undefined}
        />
        <KpiCard label={`Erstattet · ${monthLabel}`} value={money(refunded)} icon="undo" iconTone="info" />
      </div>

      {/* H8: Status-Filter-Tabs */}
      <div className="mb-5 flex flex-wrap gap-5 border-b border-outline-variant/40 px-0.5 dark:border-outline/30">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`border-b-2 pb-2.5 pt-2 font-body-sm text-[12.5px] font-semibold ${
              statusFilter === f.key
                ? "border-primary text-primary"
                : "border-transparent text-secondary hover:text-on-background dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
            }`}
          >
            {f.label} <span className="text-secondary-fixed-dim">{statusCounts[f.key]}</span>
          </button>
        ))}
      </div>

      {/* L9: echte Tabellen-Textsuche (Kunde/Beleg) — nicht die globale ⌘K-Palette */}
      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zahlungen durchsuchen (Kunde, Beleg) …"
          className="input w-full max-w-sm"
        />
      </div>

      {/* Tabelle über alle Zahlungen */}
      <div className="card overflow-hidden">
        {error && <ErrorNote message={error} />}
        {loading ? (
          <LoadingRows />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="payments"
            title="Noch keine Zahlungen"
            hint="Zahlungen legen Sie in der jeweiligen Veranstaltung an."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] border-collapse text-left">
                <thead>
                  <tr>
                    <th className="table-header">Beleg · Veranstaltung</th>
                    <th className="table-header">Kunde</th>
                    <th className="table-header text-right">Betrag</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Fällig / bezahlt</th>
                    <th className="table-header">Methode</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr
                      key={p.id}
                      className="group transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20"
                    >
                      {/* H10: Beleg-Nummer (Event-Nr.) fett + Typ, Veranstaltung als Unterzeile */}
                      <td className="border-b border-outline-variant/10 px-4 py-4 dark:border-outline/10">
                        <Link
                          to={`/veranstaltungen/${p.eventId}`}
                          className="block font-body-md text-body-md font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface"
                        >
                          {p.eventNumber ?? p.eventLabel} · {PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType}
                        </Link>
                        <p className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{p.eventLabel}</p>
                      </td>
                      <td className="border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/10">
                        {/* CX-Audit 2026-07-17: Kunde war reiner Text — jetzt Sprung ins Kundenprofil. */}
                        <Link to={`/kunden/${p.customerId}`} className="hover:text-primary hover:underline">
                          {customerName(p.customerId)}
                        </Link>
                      </td>
                      <td className="border-b border-outline-variant/10 px-4 py-4 text-right font-body-md text-body-md font-semibold text-on-background dark:border-outline/10 dark:text-inverse-on-surface">
                        {money(p.amountCents)}
                      </td>
                      <td className="border-b border-outline-variant/10 px-4 py-4 dark:border-outline/10">
                        <StatusPill meta={paymentMeta(p)} />
                      </td>
                      <td className="whitespace-nowrap border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/10">
                        {p.paidAt
                          ? formatDate(p.paidAt)
                          : p.dueDate
                            ? `fällig ${formatDate(p.dueDate)}`
                            : "–"}
                      </td>
                      <td className="whitespace-nowrap border-b border-outline-variant/10 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/10">
                        {p.stripePaymentIntentId ? (
                          <>
                            Stripe · Karte
                            <span className="ml-1 font-mono text-[10.5px] text-on-surface-variant/60">
                              {p.stripePaymentIntentId.slice(0, 8)}…{p.stripePaymentIntentId.slice(-4)}
                            </span>
                          </>
                        ) : (
                          "Überweisung"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-outline-variant/30 px-4 py-3 dark:border-outline/30">
              <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {rows.length} Zahlung{rows.length === 1 ? "" : "en"}
              </span>
              {openSum > 0 && (
                <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Offen gesamt: {money(openSum)}
                  {overdueSum > 0 && <span className="text-error"> · davon überfällig {money(overdueSum)}</span>}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
